import "./App.scss";

import { PublicClientApplication } from "@azure/msal-browser";
import { app } from "@microsoft/teams-js";
import React, { useCallback, useEffect, useState } from "react";
import { Route, Switch } from "react-router";
import { BrowserRouter } from "react-router-dom";

import { UserInfoProvider } from "./_shared/actions/useUserInfo";
import { NativeCheckoutPage } from "./_shared/components/Checkout/NativeCheckoutPage";
import { PaymentRedirectPlaceholder } from "./_shared/components/Checkout/PaymentRedirectPlaceholder";
import { ErrorBoundary } from "./_shared/components/Error/ErrorBoundary";
import { ErrorPage } from "./_shared/components/Error/ErrorPage";
import { AccountSettingsPage } from "./_shared/pages/AccountSettingsPage";
import { InstanceResultsPage } from "./_shared/pages/InstanceResultsPage";
import { SeriesAnalysisPage } from "./_shared/pages/SeriesAnalysisPage";
import { UpgradePlanPage } from "./_shared/pages/UpgradePlanPage";
import { TeamsContextProvider } from "./_shared/state/TeamsContextProvider";
import { ThemeProvider } from "./_shared/state/ThemeProvider";
import { clientEventTracker } from "./_shared/utils/clientTelemetry";
import { getAuthAppId, getPageContext } from "./_shared/utils/teamsHelpers";
import { AgilePersonalDashboardPage } from "./agile/pages/AgilePersonalDashboardPage";
import { AgileTabConfigPage } from "./agile/pages/AgileTabConfigPage";
import { AgileTabDeletePage } from "./agile/pages/AgileTabDeletePage";
import { AgileTeamTabPage } from "./agile/pages/AgileTeamTabPage";
import { AgileTrialExpired } from "./agile/pages/components/AgileTrialExpiredComponent";
import { MSALPageHost } from "./external/MSALPageHost";
import { ExternalCheckoutPage } from "./external/payment/ExternalCheckoutPage";
import { StripePaymentUpdatePage } from "./external/payment/StripePaymentUpdatePage";
import { TrialPage } from "./external/payment/TrialPage";
import { SurveyTakingPage } from "./external/survey/SurveyTakingPage";
import { NpsSurvey } from "./survey/pages/components/NpsSurvey";
import { ExportSurveyPage } from "./survey/pages/ExportSurveyPage";
import { MeetingBubblePage } from "./survey/pages/Meetings/MeetingBubblePage";
import { MeetingsTabPage } from "./survey/pages/Meetings/MeetingsTabPage";
import { QuizResultsPage } from "./survey/pages/Meetings/QuizResultsPage";
import { SurveyResultsPage } from "./survey/pages/Meetings/SurveyResultsPage";
import { StartPage } from "./survey/pages/StartPage";
import { AuthoringSuccessPage } from "./survey/pages/SurveyAuthoring/AuthoringSuccessPage";
import { PollyAuthoringPage } from "./survey/pages/SurveyAuthoring/PollyAuthoringPage";
import { SurveyEditSeriesPage } from "./survey/pages/SurveyEditSeriesPage";
import { SurveyPersonalDashboardPage } from "./survey/pages/SurveyPersonalDashboardPage";
import { SurveyTabConfigPage } from "./survey/pages/SurveyTabConfigPage";
import { SurveyTabDeletePage } from "./survey/pages/SurveyTabDeletePage";
import { SurveyTeamTabPage } from "./survey/pages/SurveyTeamTabPage";
import { SurveyTemplateTabPage } from "./survey/pages/SurveyTemplateTabPage";
import { TemplateSelection } from "./survey/pages/TemplateSelectionPage";

// MSAL configuration

export const pca = new PublicClientApplication({
  auth: {
    clientId: getAuthAppId(),
  },
});

const App: React.FC = () => {
  const defaultThemeName = getThemeFromLocationSearch() || "default";
  const [themeName, setThemeName] = useState(defaultThemeName);

  app.initialize().catch(e => {
    console.error("Failed to initialize Teams SDK", e);
    clientEventTracker.trackFatalError(
      "teams sdk init failed",
      { userId: "", tenantId: "" },
      { error: e }
    );
  });
  app.registerOnThemeChangeHandler(t => setThemeName(t));

  const [teamsContext, setTeamsContext] = useState<app.Context>();

  const setThemeNameCallback = useCallback(setThemeName, [setThemeName]);
  useEffect(() => {
    (async () => {
      const context = await app.getContext();
      setTeamsContext(context);
      if (context.app.theme) {
        setThemeNameCallback(context.app.theme);
      }
    })();
  }, [setThemeNameCallback]);

  return (
    <div className="page-container">
      <ErrorBoundary
        identity={teamsContext?.user?.id || teamsContext?.app.sessionId}
        tenantId={teamsContext?.user?.tenant?.id}
      >
        <ThemeProvider themeName={themeName} pageContext={getPageContext()}>
          <BrowserRouter>
            <Switch>
              <Route path="/(msteams|agile)/content/blank.html" exact component={MSALPageHost} />

              <Route
                path="/(msteams|agile)/content/stripe/checkout"
                exact
                component={ExternalCheckoutPage}
              />

              <Route
                path="/(msteams|agile)/content/stripe/payments/update"
                exact
                component={StripePaymentUpdatePage}
              />

              <Route
                path="/(msteams|agile)/content/marketplace/subscriptions/trial"
                exact
                component={TrialPage}
              />

              <Route
                path="/msteams/content/surveyresponse/:surveyId"
                exact
                component={SurveyTakingPage}
              />

              <TeamsContextProvider teamsContext={teamsContext}>
                <UserInfoProvider>
                  <Route path="/msteams/content/tabconfig" exact component={SurveyTabConfigPage} />
                  <Route path="/msteams/content/tabdelete" exact component={SurveyTabDeletePage} />

                  <Route path="/agile/content/tabconfig" exact component={AgileTabConfigPage} />
                  <Route path="/agile/content/tabdelete" exact component={AgileTabDeletePage} />

                  <Route
                    path="/msteams/content/tab/templates"
                    exact
                    component={SurveyTemplateTabPage}
                  />

                  <Route
                    path="/msteams/content/tab/:scope(team|personal|tenant|meetings)"
                    exact
                    component={SurveyTeamTabPage}
                  />
                  <Route
                    path="/agile/content/tab/:scope(team|personal|tenant|meetings)"
                    exact
                    component={AgileTeamTabPage}
                  />

                  <Route
                    exact
                    path="/msteams/content/personal/dashboard/:scope?"
                    component={SurveyPersonalDashboardPage}
                  />

                  <Route
                    exact
                    path="/agile/content/personal/dashboard/:scope?"
                    component={AgilePersonalDashboardPage}
                  />

                  <Route
                    path="/(msteams|agile)/content/polly/series/:seriesId"
                    exact
                    component={SeriesAnalysisPage}
                  />

                  <Route
                    path="/(msteams|agile)/content/polly/survey/:surveyId"
                    exact
                    component={InstanceResultsPage}
                  />

                  <Route
                    path="/(msteams|agile)/content/polly/survey/:surveyId/export"
                    exact
                    component={ExportSurveyPage}
                  />

                  <Route
                    path="/msteams/content/polly/:surveyId/success"
                    exact
                    component={AuthoringSuccessPage}
                  />

                  <Route
                    path="/msteams/content/polly/success"
                    exact
                    component={AuthoringSuccessPage}
                  />

                  <Route
                    path="/(msteams|agile)/content/polly/series/:seriesId/edit"
                    exact
                    component={SurveyEditSeriesPage}
                  />

                  <Route path="/agile/content/trial-expired" exact component={AgileTrialExpired} />

                  <Route
                    path="/(msteams|agile)/content/:type/authoring"
                    exact
                    component={PollyAuthoringPage}
                  />

                  <Route
                    path="/(msteams|agile)/content/subscribe"
                    exact
                    render={() => <PaymentRedirectPlaceholder trackingSource="signup-page" />}
                  />

                  <Route
                    path="/msteams/content/subscription/upgrade"
                    exact
                    component={UpgradePlanPage}
                  />

                  <Route path="/msteams/content/start" exact component={StartPage} />

                  <Route path="/msteams/content/templates" exact component={TemplateSelection} />

                  <Route path="/msteams/content/meeting/tab" exact component={MeetingsTabPage} />

                  <Route
                    path="/msteams/content/meeting/:type(poll|survey|qna|trivia|quiz|icebreaker|hot-take)/:surveyId"
                    exact
                    component={MeetingBubblePage}
                  />

                  <Route
                    path="/msteams/content/meeting/survey/:surveyId/results"
                    component={SurveyResultsPage}
                  />

                  <Route
                    path="/msteams/content/quiz/:surveyId/results"
                    component={QuizResultsPage}
                  />

                  <Route
                    path="/(msteams|agile)/content/account"
                    exact
                    component={AccountSettingsPage}
                  />
                  <Route path="/msteams/content/nps" exact component={NpsSurvey} />
                  <Route
                    path="/msteams/content/checkout/native"
                    exact
                    component={NativeCheckoutPage}
                  />
                </UserInfoProvider>
              </TeamsContextProvider>

              <Route path="*" component={ErrorPage} />
            </Switch>
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
};

function getThemeFromLocationSearch(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("theme");
}

export default App;
